import { normalizePublicWebsiteUrl } from "@/lib/websiteUrl";

export const MAX_GENERATED_IMAGE_BYTES = 5 * 1024 * 1024;
const DEFAULT_GENERATED_IMAGE_URL_MAX_LENGTH = 2_048;

export function getGeneratedImageUrl(
  value: string | null | undefined,
  maxLength = DEFAULT_GENERATED_IMAGE_URL_MAX_LENGTH,
): string | undefined {
  const normalized = value?.trim();
  if (!normalized) {
    return undefined;
  }
  if (normalized.startsWith("data:")) {
    return getGeneratedImageDataUrl(normalized);
  }
  if (Array.from(normalized).length > maxLength || !/^https?:\/\//i.test(normalized)) {
    return undefined;
  }
  try {
    const url = new URL(normalized);
    // Validate without returning the normalized value. Re-serializing a signed CDN
    // URL can alter its exact representation and invalidate the signature.
    if (url.username || url.password || !isGeneratedImageHostnamePublic(url.hostname)) {
      return undefined;
    }
    // The shared website validator intentionally rejects public IPv6 hosts. Generated
    // assets support them after the stricter address-range check above.
    if (!url.hostname.includes(":")) {
      normalizePublicWebsiteUrl(normalized);
    }
    return normalized;
  } catch {
    return undefined;
  }
}

function isGeneratedImageHostnamePublic(rawHostname: string): boolean {
  const normalizedHostname = rawHostname.replace(/^\[|\]$/g, "").toLowerCase();
  const hostname =
    normalizedHostname.endsWith(".") ? normalizedHostname.slice(0, -1) : normalizedHostname;
  if (hostname === "localhost" || hostname.endsWith(".local")) {
    return false;
  }
  if (hostname.includes(":")) {
    return !isNonPublicGeneratedImageIPv6Address(hostname);
  }
  return !isNonPublicGeneratedImageIPv4Address(hostname);
}

function isNonPublicGeneratedImageIPv6Address(hostname: string): boolean {
  // A leading compressed zero block covers unspecified, loopback, and legacy
  // IPv4-compatible/mapped forms (for example ::ffff:7f00:1).
  if (hostname.startsWith("::")) {
    return true;
  }

  const firstHextet = Number.parseInt(hostname.split(":", 1)[0] ?? "", 16);
  return (
    Number.isNaN(firstHextet) ||
    (firstHextet & 0xfe00) === 0xfc00 ||
    (firstHextet & 0xffc0) === 0xfe80 ||
    (firstHextet & 0xffc0) === 0xfec0 ||
    (firstHextet & 0xff00) === 0xff00
  );
}

function isNonPublicGeneratedImageIPv4Address(hostname: string): boolean {
  const octets = getGeneratedImageIPv4Octets(hostname);
  if (octets == null) {
    return false;
  }
  const [first, second, third] = octets;
  return (
    first === 0 ||
    first === 10 ||
    (first === 100 && second >= 64 && second <= 127) ||
    first === 127 ||
    (first === 169 && second === 254) ||
    (first === 172 && second >= 16 && second <= 31) ||
    (first === 192 && second === 168) ||
    (first === 192 && second === 0 && (third === 0 || third === 2)) ||
    (first === 192 && second === 88 && third === 99) ||
    (first === 198 && (second === 18 || second === 19)) ||
    (first === 198 && second === 51 && third === 100) ||
    (first === 203 && second === 0 && third === 113) ||
    first >= 224
  );
}

function getGeneratedImageIPv4Octets(hostname: string): [number, number, number, number] | null {
  const parts = hostname.split(".");
  if (parts.length !== 4) {
    return null;
  }
  const octets = parts.map((part) => (/^\d+$/.test(part) ? Number(part) : Number.NaN));
  if (octets.some((octet) => !Number.isInteger(octet) || octet < 0 || octet > 255)) {
    return null;
  }
  return octets as [number, number, number, number];
}

export function getGeneratedImageDataUrl(
  imageBase64: string | null | undefined,
): string | undefined {
  const value = imageBase64?.trim();
  if (!value) {
    return undefined;
  }

  const dataUrlMatch = /^data:image\/[a-z0-9.+-]+;base64,(.+)$/is.exec(value);
  const encoded = (dataUrlMatch?.[1] ?? value).replace(/\s/g, "");
  if (!isValidGeneratedImageBase64(encoded)) {
    return undefined;
  }
  const mimeType = detectGeneratedImageMimeType(encoded);
  return mimeType == null ? undefined : `data:${mimeType};base64,${encoded}`;
}

function isValidGeneratedImageBase64(value: string): boolean {
  if (value.length === 0 || value.length % 4 === 1 || !/^[a-z0-9+/]+={0,2}$/i.test(value)) {
    return false;
  }
  const paddingLength =
    value.endsWith("==") ? 2
    : value.endsWith("=") ? 1
    : 0;
  const decodedSize = Math.floor((value.length * 3) / 4) - paddingLength;
  return decodedSize > 0 && decodedSize <= MAX_GENERATED_IMAGE_BYTES;
}

function detectGeneratedImageMimeType(
  encoded: string,
): "image/jpeg" | "image/png" | "image/webp" | null {
  try {
    const prefix = atob(encoded.slice(0, Math.min(24, encoded.length)));
    const byte = (index: number) => prefix.charCodeAt(index);
    if (
      byte(0) === 0x89 &&
      byte(1) === 0x50 &&
      byte(2) === 0x4e &&
      byte(3) === 0x47 &&
      byte(4) === 0x0d &&
      byte(5) === 0x0a &&
      byte(6) === 0x1a &&
      byte(7) === 0x0a
    ) {
      return "image/png";
    }
    if (byte(0) === 0xff && byte(1) === 0xd8 && byte(2) === 0xff) {
      return "image/jpeg";
    }
    if (prefix.slice(0, 4) === "RIFF" && prefix.slice(8, 12) === "WEBP") {
      return "image/webp";
    }
  } catch {
    return null;
  }
  return null;
}
